import { StandardEvents } from '@shopify/events';

/**
 * <amq-product-hinweise>: the note boxes under the Härtegrad row
 * (blocks/amq_product_hinweise.liquid). Each box carries its rules as data
 * attributes: the product it belongs to, a minimum width, a Härtegrad and an
 * optional replacement for the model card's subtitle. The component reads the
 * chosen width and Härtegrad from the variant picker and the active model from
 * Malte's model switcher, shows the boxes that apply, fills the placeholders
 * {breite} / {hälfte} and swaps the card subtitle while a note applies.
 *
 * Re-evaluation is event driven: `change` on the picker's radios (immediate),
 * Horizon's product-select event whose promise resolves once the section has
 * been re-rendered (the markup comes back pre-evaluated for the page product
 * only), and a click on a switcher button. The switcher loads the other model
 * asynchronously and marks the section with data-amq-model-loading meanwhile,
 * so the click handler waits for that flag to clear before evaluating.
 */
const WIDTH_PLACEHOLDER = /\{breite\}/g;
const HALF_PLACEHOLDER = /\{h(?:ä|ae)lfte\}/g;
const LOADING_ATTRIBUTE = 'data-amq-model-loading';
const SWITCH_TIMEOUT = 5000;

if (!customElements.get('amq-product-hinweise')) {
  customElements.define(
    'amq-product-hinweise',
    class AmqProductHinweise extends HTMLElement {
      /** @type {Element | null} */
      #section = null;

      connectedCallback() {
        this.#section = this.closest('.shopify-section');
        document.addEventListener('change', this.#onChange, true);
        document.addEventListener('click', this.#onClick, true);
        this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
        this.#apply();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.#onChange, true);
        document.removeEventListener('click', this.#onClick, true);
        this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
      }

      /**
       * A pill was chosen: the picker's radios are already updated at this point.
       * @param {Event} event
       */
      #onChange = (event) => {
        if (event.target instanceof Element && event.target.closest('variant-picker')) this.#apply();
      };

      /**
       * Horizon re-renders the section after a selection; the event carries a promise for it.
       * @param {CustomEvent & { promise?: Promise<unknown> }} event
       */
      #onProductSelect = (event) => {
        event.promise?.then(() => this.#apply()).catch(() => {});
      };

      /**
       * A switcher button was clicked: wait until the other model's markup has arrived.
       * @param {Event} event
       */
      #onClick = (event) => {
        const button = event.target instanceof Element ? event.target.closest('amq-model-switcher button[data-product-url]') : null;
        if (!button) return;

        const started = Date.now();
        const check = () => {
          const loading = this.#section?.hasAttribute(LOADING_ATTRIBUTE);
          if (!loading || Date.now() - started > SWITCH_TIMEOUT) {
            this.#apply();
            return;
          }
          setTimeout(check, 100);
        };
        setTimeout(check, 150);
      };

      /** The model whose picker is on the page: the pressed switcher button, else the page product. */
      #activeProductId() {
        const pressed = document.querySelector('amq-model-switcher [aria-pressed="true"][data-product-id]');
        return pressed instanceof HTMLElement ? pressed.dataset.productId ?? '' : this.dataset.productId ?? '';
      }

      /** Chosen width in cm and Härtegrad digit from the picker's checked radios (null when unknown). */
      #selection() {
        const picker = document.querySelector('variant-picker');
        /** @type {{ width: number | null, grade: string | null }} */
        const selection = { width: null, grade: null };
        if (!picker) return selection;

        for (const input of picker.querySelectorAll('fieldset input[type="radio"]:checked')) {
          if (!(input instanceof HTMLInputElement)) continue;
          const name = input.dataset.optionName ?? '';
          if (/^breite/i.test(name)) {
            const width = Number.parseInt(input.value, 10);
            if (!Number.isNaN(width)) selection.width = width;
          } else if (/^h[äa]rtegrad$/i.test(name)) {
            const match = input.value.match(/\d/);
            if (match) selection.grade = match[0];
          }
        }
        return selection;
      }

      #apply() {
        const productId = this.#activeProductId();
        const { width, grade } = this.#selection();
        /** @type {Map<string, string>} subtitle replacement per product card */
        const subtitles = new Map();

        for (const note of this.querySelectorAll('[data-amq-hinweis]')) {
          if (!(note instanceof HTMLElement)) continue;
          const minWidth = Number.parseInt(note.dataset.minWidth ?? '', 10);
          const ruleGrade = note.dataset.grade ?? '';
          let visible = note.dataset.productId === productId;
          if (visible && !Number.isNaN(minWidth)) visible = width !== null && width >= minWidth;
          if (visible && ruleGrade) visible = grade === ruleGrade;

          note.hidden = !visible;
          if (visible) this.#fill(note, width);
          if (visible && note.dataset.cardSubtitle) subtitles.set(note.dataset.productId ?? '', note.dataset.cardSubtitle);
        }

        this.#swapSubtitles(subtitles);
      }

      /**
       * Writes the template text with the placeholders filled into the output element.
       * @param {HTMLElement} note
       * @param {number | null} width
       */
      #fill(note, width) {
        const template = note.querySelector('template[data-amq-hinweis-template]');
        const output = note.querySelector('[data-amq-hinweis-output]');
        if (!(template instanceof HTMLTemplateElement) || !output) return;

        const widthText = width === null ? '' : String(width);
        const half = width === null ? '' : (width / 2).toLocaleString('de-DE', { maximumFractionDigits: 1 });
        const html = template.innerHTML.replace(WIDTH_PLACEHOLDER, widthText).replace(HALF_PLACEHOLDER, half);
        if (output.innerHTML !== html) output.innerHTML = html;
      }

      /**
       * Replaces the subtitle of the model cards named in `subtitles` and restores the others.
       * @param {Map<string, string>} subtitles
       */
      #swapSubtitles(subtitles) {
        for (const card of document.querySelectorAll('amq-model-switcher .amq-model-switcher__option[data-product-id]')) {
          if (!(card instanceof HTMLElement)) continue;
          const subtitle = card.querySelector('.amq-model-switcher__subtitle');
          if (!(subtitle instanceof HTMLElement)) continue;

          const replacement = subtitles.get(card.dataset.productId ?? '');
          if (replacement) {
            if (!('amqOriginal' in subtitle.dataset)) subtitle.dataset.amqOriginal = subtitle.textContent ?? '';
            if (subtitle.textContent !== replacement) subtitle.textContent = replacement;
          } else if ('amqOriginal' in subtitle.dataset) {
            subtitle.textContent = subtitle.dataset.amqOriginal ?? '';
            delete subtitle.dataset.amqOriginal;
          }
        }
      }
    }
  );
}
